import React, { useState, useRef } from "react";
import { useMutation } from "@apollo/client";
import {
  MARK_EMAIL_UNREAD,
  MARK_EMAIL_FLAG,
} from "../../../../utils/GraphQL/mutations";
import FlagIcon from "@mui/icons-material/Flag";
import BdrMenu from "../../../../components/BdrMenu/BdrMenu";

/**
 * EmailFlags — the per-row read/unread + flag menu.
 *
 * ─────────────────────────────────────────────────────────────────
 * IT USES `BdrMenu`, NOT MUI's `Menu` (2026-10-06). The MUI one rendered with
 * its default palette — a white sheet with black text — hanging off a dark
 * navy row, because `emailFlags.css` was an empty file and nothing ever
 * styled it. `BdrMenu` is the same menu the admin navbar drops, so this now
 * matches every other list in the app instead of being the one that doesn't.
 *
 * THE ANCHOR IS BLURRED ON OPEN, and that is a real bug fix, not tidying.
 * The row's quick-action rail is revealed by hover or focus. A mouse click
 * left focus sitting on this button, so after the menu closed the row stayed
 * lit as though it were still hovered, and the next click anywhere was spent
 * clearing that rather than doing what it looked like it would do. `BdrMenu`
 * moves focus into itself while open, so dropping it here costs the keyboard
 * path nothing. (The row CSS also switched to `:focus-visible`, which a mouse
 * click never sets — the two fixes are belt and braces on the same symptom.)
 */
const EmailFlags = ({ emailId, emailRead, handleSnack, getSelectedEmails }) => {
  const [flagAnchorEl, setFlagAnchorEl] = useState(null);
  const [markUnread] = useMutation(MARK_EMAIL_UNREAD);
  const [markFlag] = useMutation(MARK_EMAIL_FLAG);
  const emailFlagRef = useRef();

  const handleCloseSubMenu = () => {
    setFlagAnchorEl(null);
  };

  const handleMarkUnread = async () => {
    const { data: unread } = await markUnread({
      variables: { emailId: emailId, read: !emailRead },
    });

    if (unread?.markEmailUnread) {
      if (handleSnack) {
        handleSnack("Email Marked Unread", 2000);
      }
      if (getSelectedEmails) {
        getSelectedEmails();
      }
      handleCloseSubMenu();
      return;
    }

    if (handleSnack) {
      handleSnack("Error...Email not modified.", 3000);
    }

    handleCloseSubMenu();
    return;
  };

  const handleFlag = async (flagValue) => {
    const { data } = await markFlag({
      variables: {
        emailId: emailId,
        flag: flagValue,
      },
    });

    const flagSnack = () => {
      let value = "Flag Removed";

      if (flagValue === "!") {
        value = "Flagged Important";
      }

      if (flagValue === "!!") {
        value = "Flagged Urgent";
      }

      return value;
    };

    if (data?.changeEmailFlag) {
      if (handleSnack) {
        handleSnack(`Email ${flagSnack()}`, 2000);
      }
      if (getSelectedEmails) {
        getSelectedEmails();
      }
      handleCloseSubMenu();
      return;
    }

    if (handleSnack) {
      handleSnack("Error...Email not modified.", 3000);
    }

    handleCloseSubMenu();
    return;
  };

  return (
    <>
      <button
        ref={emailFlagRef}
        className="email-page-action-img-button"
        title="Read state and flags"
        aria-label="Read state and flags"
        aria-haspopup="menu"
        onClick={(event) => {
          const anchor = event.currentTarget;
          setFlagAnchorEl(anchor);
          /* See the header note: the menu takes focus from here, so letting
             this keep it only leaves the row looking selected afterwards. */
          anchor.blur();
        }}
      >
        <FlagIcon />
      </button>

      <BdrMenu
        anchorEl={flagAnchorEl}
        onClose={handleCloseSubMenu}
        ariaLabel="Read state and flags"
        items={[
          {
            key: "read",
            label: emailRead ? "Mark Unread" : "Mark Read",
            onClick: () => handleMarkUnread(),
          },
          { key: "d1", divider: true },
          {
            key: "important",
            label: "Important",
            onClick: () => handleFlag("!"),
          },
          {
            key: "urgent",
            label: "Urgent",
            onClick: () => handleFlag("!!"),
          },
          {
            key: "clear",
            label: "Remove Flag",
            onClick: () => handleFlag(""),
          },
        ]}
      />
    </>
  );
};

export default EmailFlags;
